import { Alert, Autocomplete, Box, Button, Divider, Paper, Stack, TextField } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchDirectCalendar } from './DirectCalendarApi';
import DirectCalendarSection from './DirectCalendarSection';
import CalendarDateNav from './CalendarDateNav';
import StandingsPage from '../../pages/ranking/StandingsPage';
import './DirectCalendarWidget.css';

export default function DirectCalendarWidget({
  widgetConfigurationId,
  date,
  discipline = null,
  competitionIds = null,
  hasCompetitionIdsParam = false,
  reloadKey = 0,
  onRefresh,
  refreshDisabled = false,
  competitionFilterPlacement = 'inline',
  competitionFilterPortalTarget = null,
  onCompetitionFilterVisibilityChange,
  onCompetitionIdsChange,
  onDateChange,
  onNavigate,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [standingsParams, setStandingsParams] = useState(null);

  const [localDate, setLocalDate] = useState(null);
  const [localCompetitionIds, setLocalCompetitionIds] = useState([]);
  const [localHasCompetitionIdsParam, setLocalHasCompetitionIdsParam] = useState(false);

  const effectiveDate = date || localDate;
  const isCompetitionControlled = Array.isArray(competitionIds);

  const effectiveCompetitionIds = isCompetitionControlled
    ? competitionIds
    : localCompetitionIds;

  const effectiveHasCompetitionIdsParam = isCompetitionControlled
    ? hasCompetitionIdsParam
    : localHasCompetitionIdsParam;

  const competitionIdsKey = effectiveCompetitionIds.join(',');

  const handleDateChange = (nextDate) => {
    setStandingsParams(null);

    if (onDateChange) {
      onDateChange(nextDate);
      return;
    }

    setLocalDate(nextDate);
  };

  const handleCompetitionChange = (_, value) => {
    const nextIds = Array.isArray(value)
      ? value.map((competition) => String(competition._id)).filter(Boolean)
      : [];

    setStandingsParams(null);
    setLocalDate(null);

    if (onCompetitionIdsChange) {
      onCompetitionIdsChange(nextIds);
      return;
    }

    setLocalHasCompetitionIdsParam(true);
    setLocalCompetitionIds(nextIds);
  };

  const handleOpenStandings = ({ competitionId, day }) => {
    if (!competitionId || !day) return;

    setStandingsParams({
      competitionId: String(competitionId),
      day: String(day),
    });
  };

  const handleCloseStandings = () => {
    setStandingsParams(null);
  };

  useEffect(() => {
    let ignore = false;

    const load = async () => {
      if (!widgetConfigurationId) {
        setLoading(false);
        setError('Configuration widget manquante.');
        setData(null);
        return;
      }

      try {
        setLoading(true);
        setError('');

        const result = await fetchDirectCalendar({
          widgetConfigurationId,
          date: effectiveDate,
          discipline,
          competitionIds: effectiveCompetitionIds,
          hasCompetitionIdsParam: effectiveHasCompetitionIdsParam,
        });

        if (!ignore) {
          setData(result);
        }
      } catch {
        if (!ignore) {
          setError('Impossible de charger le calendrier.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      ignore = true;
    };
  }, [
    widgetConfigurationId,
    effectiveDate,
    discipline,
    competitionIdsKey,
    effectiveHasCompetitionIdsParam,
    reloadKey,
  ]);

  useEffect(() => {
    setLocalDate(null);
    setStandingsParams(null);

    if (!isCompetitionControlled) {
      setLocalCompetitionIds([]);
      setLocalHasCompetitionIdsParam(false);
    }
  }, [discipline, isCompetitionControlled]);

  useEffect(() => {
    setStandingsParams(null);
  }, [reloadKey]);

  const groups = Array.isArray(data?.items) ? data.items : [];

  const hasCompetitionData = (competition) =>
    Array.isArray(competition?.matches) && competition.matches.length > 0;

  const visibleGroups = groups
    .map((group) => ({
      ...group,
      competitions: Array.isArray(group?.competitions)
        ? group.competitions.filter(hasCompetitionData)
        : [],
    }))
    .filter((group) => group.competitions.length > 0);

  const showDisciplineTitle = visibleGroups.length > 1;
  const hasVisibleMatches = visibleGroups.length > 0;

  const competitionOptions = Array.isArray(data?.competitionOptions)
    ? data.competitionOptions
    : [];

  const backendSelectedCompetitionIds = Array.isArray(data?.selectedCompetitionIds)
    ? data.selectedCompetitionIds.map((id) => String(id))
    : [];

  const displayedCompetitionIds =
    effectiveCompetitionIds.length > 0
      ? effectiveCompetitionIds
      : !effectiveHasCompetitionIdsParam
      ? backendSelectedCompetitionIds
      : [];

  const selectedCompetitionOptions = competitionOptions.filter((competition) =>
    displayedCompetitionIds.includes(String(competition._id))
  );

  const showCompetitionInput = !error && Boolean(data) && competitionOptions.length > 1;

  const getCompetitionLabel = (competition) => {
    if (!competition) return '';

    const name = competition.name || '';
    const season = competition.season || '';

    return season ? `${name} (${season})` : name;
  };

  const competitionFilterContent = showCompetitionInput ? (
    <Box className="stats-builder__filter-input">
      <div className="stats-builder__filter-header">
        <p className="stats-builder__filter-title">Compétitions</p>
        <p className="stats-builder__filter-caption">
          Filtre les matchs et les dates disponibles du calendrier.
        </p>
      </div>

      <Divider />

      <Autocomplete
        multiple
        className="direct-calendar-widget__competition-filter"
        options={competitionOptions}
        value={selectedCompetitionOptions}
        onChange={handleCompetitionChange}
        getOptionLabel={getCompetitionLabel}
        isOptionEqualToValue={(option, value) =>
          String(option._id) === String(value._id)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label=""
            placeholder={
              selectedCompetitionOptions.length > 0
                ? ''
                : 'Toutes les compétitions'
            }
          />
        )}
      />
    </Box>
  ) : null;

  const competitionFilterPanel = competitionFilterContent ? (
    <Paper
      elevation={0}
      variant="outlined"
      className="stats-builder-panel"
      sx={{
        borderRadius: 3,
        borderColor: '#e5e7eb',
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={2} className="stats-builder-panel__content_inner">
        <Box className="stats-builder-panel__content">
          {competitionFilterContent}
        </Box>
      </Stack>
    </Paper>
  ) : null;

  const shouldPortalCompetitionFilter = competitionFilterPlacement === 'portal';

  const competitionFilterPortal =
    competitionFilterPortalTarget && competitionFilterContent
      ? createPortal(competitionFilterContent, competitionFilterPortalTarget)
      : null;

  useEffect(() => {
    if (!onCompetitionFilterVisibilityChange) return undefined;

    onCompetitionFilterVisibilityChange(showCompetitionInput);

    return () => {
      onCompetitionFilterVisibilityChange(false);
    };
  }, [showCompetitionInput, onCompetitionFilterVisibilityChange]);

  if (loading) return shouldPortalCompetitionFilter ? competitionFilterPortal : null;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  return (
    <Box className="direct-calendar-widget">
      {shouldPortalCompetitionFilter ? competitionFilterPortal : competitionFilterPanel}


      <CalendarDateNav
        selectedDate={data.selectedDate}
        previousDate={data.previousDate}
        nextDate={data.nextDate}
        previousDates={data.previousDates || []}
        nextDates={data.nextDates || []}
        availableDates={data.availableDates || []}
        onDateChange={handleDateChange}
      />

      
      <Stack direction="row" justifyContent="flex-end" className="stats-builder-refresh">
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          disabled={refreshDisabled}
          onClick={onRefresh}
        >
          Actualiser
        </Button>
      </Stack>

      {!data.selectedDate ? (
        <Alert severity="info" className="direct-calendar-widget__alert">
          Aucun match disponible sur la période configurée.
        </Alert>
      ) : null}

      {data.selectedDate && !hasVisibleMatches ? (
        <Alert severity="info" className="direct-calendar-widget__alert">
          Aucun match prévu pour cette date.
        </Alert>
      ) : null}

      {standingsParams ? (
        <Box className="direct-calendar-widget__standings-view">
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={handleCloseStandings}
            className="direct-calendar-widget__back-button"
          >
            Retour au calendrier
          </Button>

          <StandingsPage
            embedded
            competitionIds={[standingsParams.competitionId]}
            day={standingsParams.day}
            widgetConfigurationId=""
            title=""
            showCompetitionInfos={false}
            onNavigate={onNavigate}
          />
        </Box>
      ) : hasVisibleMatches ? (
        <Stack className="direct-calendar-widget__groups">
          {visibleGroups.map((disciplineGroup) => (
            <DirectCalendarSection
              key={disciplineGroup.discipline}
              group={disciplineGroup}
              showDisciplineTitle={showDisciplineTitle}
              onNavigate={onNavigate}
              onStandingsClick={handleOpenStandings}
            />
          ))}
        </Stack>
      ) : null}
    </Box>
  );
}
